import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { CheckCircle2, AlertTriangle, AlertCircle, HelpCircle, ArrowRight } from 'lucide-react';
import { cn, formatNumber } from '@/lib/utils';

interface Props {
  data: {
    al_dia: number;
    proximos: number;
    vencidos: number;
    sin_programar: number;
  };
  loading?: boolean;
}

const ROWS = [
  { key: 'vencidos',      label: 'Vencidos',          icon: AlertCircle,   tone: 'bg-red-100 text-red-700 ring-red-200',         filter: 'DANGER' },
  { key: 'proximos',      label: 'Próximos a vencer', icon: AlertTriangle, tone: 'bg-amber-100 text-amber-800 ring-amber-200',   filter: 'WARN' },
  { key: 'al_dia',        label: 'Al día',            icon: CheckCircle2,  tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200', filter: 'OK' },
  { key: 'sin_programar', label: 'Sin programar',     icon: HelpCircle,    tone: 'bg-slate-100 text-slate-700 ring-slate-200',   filter: 'SIN_PROGRAMAR' },
] as const;

export function SemaforoSummary({ data, loading }: Props) {
  const total = data.al_dia + data.proximos + data.vencidos + data.sin_programar;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">Semáforo de mantenimientos</CardTitle>
          <CardDescription>Estado de los {formatNumber(total)} activos del catálogo</CardDescription>
        </div>
        <Link
          to="/mantenimiento"
          className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline"
        >
          Ver todos <ArrowRight className="h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {ROWS.map((row) => {
          const value = data[row.key];
          const pct = total > 0 ? (value / total) * 100 : 0;
          const Icon = row.icon;
          return (
            <Link
              key={row.key}
              to={`/mantenimiento?semaforo=${row.filter}`}
              className="flex items-center gap-3 group hover:bg-muted/40 -mx-2 px-2 py-1.5 rounded-md transition-colors"
            >
              <div className={cn('h-8 w-8 rounded-lg ring-1 flex items-center justify-center shrink-0', row.tone)}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{row.label}</p>
                  {loading ? (
                    <div className="skeleton h-4 w-10 rounded" />
                  ) : (
                    <p className="text-sm font-semibold tabular">{formatNumber(value)}</p>
                  )}
                </div>
                {/* Barra de proporción */}
                <div className="mt-1 h-1 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all duration-700',
                      row.key === 'vencidos'      && 'bg-red-500',
                      row.key === 'proximos'      && 'bg-amber-500',
                      row.key === 'al_dia'        && 'bg-emerald-500',
                      row.key === 'sin_programar' && 'bg-slate-400',
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
